import React, { useState } from 'react';
import CommonModal from '../../Common/CommonModal';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';

const CreateRackModal = ({
  isOpen,
  onClose,
  warehouses = [],
  onRackCreated,
  onRackUpdated,
  editingRack = null
}) => {
  const [formData, setFormData] = useState({
    store: '',
    rackNumber: '',
    name: '',
    zone: 'A',
    aisle: '',
    description: '',
    levels: [
      {
        levelNumber: '1',
        name: 'Level 1',
        positions: [
          { positionNumber: 'A1', name: 'Pos A1', barcode: '', maxWeightKg: 0 }
        ]
      }
    ]
  });

  React.useEffect(() => {
    if (editingRack) {
      setFormData({
        store: editingRack.store?._id || editingRack.store || '',
        rackNumber: editingRack.rackNumber || '',
        name: editingRack.name || '',
        zone: editingRack.zone || 'A',
        aisle: editingRack.aisle || '',
        description: editingRack.description || '',
        levels: editingRack.levels && editingRack.levels.length > 0 ? editingRack.levels : [
          {
            levelNumber: '1',
            name: 'Level 1',
            positions: [{ positionNumber: 'A1', name: 'Pos A1', barcode: '', maxWeightKg: 0 }]
          }
        ]
      });
    } else {
      setFormData({
        store: '',
        rackNumber: '',
        name: '',
        zone: 'A',
        aisle: '',
        description: '',
        levels: [
          {
            levelNumber: '1',
            name: 'Level 1',
            positions: [{ positionNumber: 'A1', name: 'Pos A1', barcode: '', maxWeightKg: 0 }]
          }
        ]
      });
    }
  }, [editingRack, isOpen]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'preview'

  const handleLevelChange = (lvlIdx, field, value) => {
    const updated = [...formData.levels];
    updated[lvlIdx][field] = value;
    setFormData({ ...formData, levels: updated });
  };

  const addLevel = () => {
    const nextLvlNum = (formData.levels.length + 1).toString();
    const newLvl = {
      levelNumber: nextLvlNum,
      name: `Level ${nextLvlNum}`,
      positions: [
        { positionNumber: 'A1', name: 'Pos A1', barcode: '', maxWeightKg: 0 }
      ]
    };
    setFormData({ ...formData, levels: [...formData.levels, newLvl] });
  };

  const removeLevel = (lvlIdx) => {
    if (formData.levels.length <= 1) return;
    const updated = formData.levels.filter((_, idx) => idx !== lvlIdx);
    setFormData({ ...formData, levels: updated });
  };

  const addPosition = (lvlIdx) => {
    const updated = [...formData.levels];
    const posCount = updated[lvlIdx].positions.length + 1;
    updated[lvlIdx].positions.push({
      positionNumber: `A${posCount}`,
      name: `Pos A${posCount}`,
      barcode: '',
      maxWeightKg: 0
    });
    setFormData({ ...formData, levels: updated });
  };

  const removePosition = (lvlIdx, posIdx) => {
    const updated = [...formData.levels];
    if (updated[lvlIdx].positions.length <= 1) return;
    updated[lvlIdx].positions = updated[lvlIdx].positions.filter((_, idx) => idx !== posIdx);
    setFormData({ ...formData, levels: updated });
  };

  const handlePositionChange = (lvlIdx, posIdx, field, value) => {
    const updated = [...formData.levels];
    updated[lvlIdx].positions[posIdx][field] = value;
    setFormData({ ...formData, levels: updated });
  };

  // Quick generator helper: quickly build N levels with M positions
  const [quickGen, setQuickGen] = useState({ levelsCount: 3, positionsPerLevel: 4 });
  const applyQuickGenerator = () => {
    const numLevels = Math.max(1, Math.min(20, parseInt(quickGen.levelsCount) || 1));
    const numPositions = Math.max(1, Math.min(50, parseInt(quickGen.positionsPerLevel) || 1));

    const generatedLevels = [];
    for (let l = 1; l <= numLevels; l++) {
      const positions = [];
      for (let p = 1; p <= numPositions; p++) {
        positions.push({
          positionNumber: `P${p}`,
          name: `Pos P${p}`,
          barcode: '',
          maxWeightKg: 0
        });
      }
      generatedLevels.push({
        levelNumber: `${l}`,
        name: `Level ${l}`,
        positions
      });
    }

    setFormData(prev => ({ ...prev, levels: generatedLevels }));
  };

  const totalPositionsCount = formData.levels.reduce((sum, l) => sum + (l.positions?.length || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.store) {
      setError('Please select a Store or Warehouse');
      return;
    }
    if (!formData.rackNumber.trim()) {
      setError('Please enter a unique Rack Number');
      return;
    }

    // Check duplicate level identifiers
    const lvlNums = new Set();
    for (const lvl of formData.levels) {
      const cleanLvl = (lvl.levelNumber || '').toString().trim().toUpperCase();
      if (!cleanLvl) {
        setError('Level identifier cannot be empty');
        return;
      }
      if (lvlNums.has(cleanLvl)) {
        setError(`Duplicate level identifier "${cleanLvl}" found`);
        return;
      }
      lvlNums.add(cleanLvl);

      const posNums = new Set();
      for (const pos of lvl.positions) {
        const cleanPos = (pos.positionNumber || '').toString().trim().toUpperCase();
        if (!cleanPos) {
          setError(`Position identifier in level "${cleanLvl}" cannot be empty`);
          return;
        }
        if (posNums.has(cleanPos)) {
          setError(`Duplicate position "${cleanPos}" in level "${cleanLvl}"`);
          return;
        }
        posNums.add(cleanPos);
      }
    }

    try {
      setLoading(true);
      if (editingRack) {
        await onRackUpdated(editingRack._id, formData);
      } else {
        await onRackCreated(formData);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save rack');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title={editingRack ? `Edit Storage Rack: ${editingRack.rackNumber}` : "Create New Storage Rack"}
      size="xl"
      icon={
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7M4 7c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3M4 7h16M7 11h10M7 15h10" />
        </svg>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Tab switcher: Configuration vs Preview */}
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'config'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            1. Rack Configuration
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-4 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors ${
              activeTab === 'preview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>2. Structure Preview</span>
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
              {formData.levels.length} Levels / {totalPositionsCount} Positions
            </span>
          </button>
        </div>

        {activeTab === 'config' && (
          <div className="space-y-6">
            {/* Hierarchy banner */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">
                ERP
              </div>
              <div className="text-xs text-gray-700 flex-1">
                <span className="font-semibold text-indigo-900">Hierarchy Flow:</span>{' '}
                <span className="font-medium text-gray-900">Store / Warehouse</span> →{' '}
                <span className="font-semibold text-indigo-700">Rack</span> →{' '}
                <span className="font-semibold text-purple-700">Level</span> →{' '}
                <span className="font-semibold text-green-700">Position</span>
              </div>
            </div>

            {/* Basic details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <CustomSelect
                  label="Store / Warehouse"
                  required
                  value={formData.store}
                  onChange={(e) => setFormData({ ...formData, store: e.target.value })}
                  placeholder="Select Store/Warehouse"
                  options={warehouses.map(w => ({
                    value: w._id,
                    label: `${w.name} (${w.code || 'Store'})`
                  }))}
                />
              </div>

              <div>
                <TextInput
                  label="Rack Number / Code"
                  required
                  placeholder="e.g. RK-A01, RACK-10"
                  value={formData.rackNumber}
                  onChange={(e) => setFormData({ ...formData, rackNumber: e.target.value.toUpperCase() })}
                  helperText="Must be unique within the store"
                />
              </div>

              <div>
                <TextInput
                  label="Rack Name / Label (Optional)"
                  placeholder="e.g. East Aisle Heavy Parts"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <TextInput
                  label="Zone"
                  placeholder="e.g. Zone A, High-Bay"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                />
              </div>
              <div>
                <TextInput
                  label="Aisle"
                  placeholder="e.g. Aisle 04"
                  value={formData.aisle}
                  onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                />
              </div>
              <div>
                <TextInput
                  label="Description / Location Notes"
                  placeholder="e.g. Standard 4-tier steel pallet racking"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Quick Generator Box */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Quick Rack Matrix Generator</h4>
                  <p className="text-xs text-gray-500">Auto-generate standard grid of levels and positions instantly</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-gray-700">
                    <span>Levels:</span>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={quickGen.levelsCount}
                      onChange={(e) => setQuickGen({ ...quickGen, levelsCount: e.target.value })}
                      className="w-16 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-center font-bold"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-700">
                    <span>Pos/Level:</span>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={quickGen.positionsPerLevel}
                      onChange={(e) => setQuickGen({ ...quickGen, positionsPerLevel: e.target.value })}
                      className="w-16 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-center font-bold"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={applyQuickGenerator}
                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-semibold shadow-sm transition-colors"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>

            {/* Levels and Positions builder */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <span>Rack Levels &amp; Positions</span>
                  <span className="text-xs font-normal text-gray-500">
                    ({formData.levels.length} levels, {totalPositionsCount} total positions)
                  </span>
                </h4>
                <button
                  type="button"
                  onClick={addLevel}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs font-semibold transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Level
                </button>
              </div>

              <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                {formData.levels.map((level, lvlIdx) => (
                  <div
                    key={lvlIdx}
                    className="p-3.5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-indigo-300 transition-all space-y-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="w-6 h-6 rounded-md bg-purple-100 text-purple-700 font-bold text-xs flex items-center justify-center">
                          L{lvlIdx + 1}
                        </span>
                        <div className="w-28">
                          <input
                            type="text"
                            placeholder="Level No."
                            value={level.levelNumber}
                            onChange={(e) => handleLevelChange(lvlIdx, 'levelNumber', e.target.value.toUpperCase())}
                            className="w-full px-2 py-1 text-xs font-bold border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                            required
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Level Description / Name"
                            value={level.name}
                            onChange={(e) => handleLevelChange(lvlIdx, 'name', e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => addPosition(lvlIdx)}
                          className="px-2.5 py-1 text-xs bg-green-50 text-green-700 hover:bg-green-100 font-medium rounded transition-colors"
                        >
                          + Pos
                        </button>
                        {formData.levels.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLevel(lvlIdx)}
                            className="p-1 text-red-500 hover:text-red-700 rounded transition-colors"
                            title="Remove Level"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Positions Grid */}
                    <div className="pl-8 pt-1 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {level.positions.map((pos, posIdx) => (
                          <div
                            key={posIdx}
                            className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1 text-xs group hover:bg-indigo-50/50"
                          >
                            <input
                              type="text"
                              value={pos.positionNumber}
                              onChange={(e) => handlePositionChange(lvlIdx, posIdx, 'positionNumber', e.target.value.toUpperCase())}
                              className="w-14 px-1.5 py-0.5 text-xs text-center font-bold bg-white border border-gray-200 rounded"
                              placeholder="Pos No"
                              required
                            />
                            {level.positions.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePosition(lvlIdx, posIdx)}
                                className="text-gray-400 hover:text-red-600 p-0.5"
                                title="Remove Position"
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Structure Preview Tab */}
        {activeTab === 'preview' && (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-gray-900 to-indigo-950 text-white rounded-2xl shadow-inner">
              <div className="flex items-center justify-between pb-3 border-b border-gray-800">
                <div>
                  <span className="text-xs uppercase tracking-wider text-indigo-400 font-bold">Rack Blueprint</span>
                  <h3 className="text-lg font-extrabold text-white">
                    {formData.rackNumber || 'UNNAMED RACK'} {formData.name && `— ${formData.name}`}
                  </h3>
                </div>
                <div className="text-right text-xs text-gray-300">
                  <div>Store: <span className="font-bold text-white">{warehouses.find(w => w._id === formData.store)?.name || 'Not selected'}</span></div>
                  <div>Zone: <span className="font-bold text-white">{formData.zone || 'A'}</span> | Aisle: <span className="font-bold text-white">{formData.aisle || '—'}</span></div>
                </div>
              </div>

              {/* Physical Rack Visualizer */}
              <div className="mt-4 space-y-3">
                {formData.levels.map((lvl, lIdx) => (
                  <div key={lIdx} className="bg-gray-800/80 border border-indigo-500/30 rounded-xl p-3">
                    <div className="flex items-center justify-between text-xs text-indigo-300 font-semibold mb-2">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                        Level {lvl.levelNumber} ({lvl.name || 'Standard Tier'})
                      </span>
                      <span className="text-gray-400">{lvl.positions.length} Positions</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {lvl.positions.map((pos, pIdx) => (
                        <div
                          key={pIdx}
                          className="bg-gray-900/90 border border-green-500/40 rounded-lg p-2 text-center text-xs flex flex-col justify-between hover:border-green-400 transition-colors"
                        >
                          <span className="font-extrabold text-green-300">{pos.positionNumber}</span>
                          <span className="text-[10px] text-gray-400 mt-1 truncate">{pos.name || 'Empty'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving Rack...</span>
              </>
            ) : (
              <span>Save Rack Structure</span>
            )}
          </button>
        </div>
      </form>
    </CommonModal>
  );
};

export default CreateRackModal;
